import { LeadBatchListResponse } from '@herbjoy/apex-api-models';
import angular, { IHttpService, IPromise } from 'angular'
import moment from 'moment'

import { mapLeadSnapshotVM, hydrateLeadMarkerList } from './leads.service'

interface LeadBatchGetRequest {
    startDate?: Date;
    endDate?: Date;
    divisionID?: number;
}

export interface LeadBatchService {
    // TODO: Convert the factory into a service instead of making it conform to an interface.
    retrieveLeadBatches(query?: LeadBatchGetRequest): IPromise<LeadBatchListResponse[]>;
}

export function LeadBatchServiceFactory($http: IHttpService, apexApiUrl: string): LeadBatchService {
    'ngInject'
    const service = {
        retrieveLeadBatches: retrieveLeadBatches,
        retrieveLeadBatchJob: retrieveLeadBatchJob,
        retrieveLeadBatch: retrieveLeadBatch,
        createLeadBatch: createLeadBatch,
        retrieveLeadGroups: retrieveLeadGroups,
        retrieveLeadGroup: retrieveLeadGroup,
        patchLeadGroup: patchLeadGroup,
        deleteLeadBatch: deleteLeadBatch,
        removeLeadGroupGeography: removeLeadGroupGeography,
        createLeadGroupGeography: createLeadGroupGeography,
        updateLeadGroupGeography: updateLeadGroupGeography,
        createLeadTerritory: createLeadTerritory,
        updateLeadTerritory: updateLeadTerritory,
        removeLeadTerritory: removeLeadTerritory,
        retrieveLeadGroupTerritories: retrieveLeadGroupTerritories,
        retrieveLeadGroupLeadsReport: retrieveLeadGroupLeadsReport,
        retrieveLeadGroupLeads: retrieveLeadGroupLeads,
        retrieveLeadTerritoryOffices: retrieveLeadTerritoryOffices,
        retrieveLeadGroupGeographiesOffices: retrieveLeadGroupGeographiesOffices,
        retrieveLeadTerritories: retrieveLeadTerritories,
        retrieveLeadTerritory: retrieveLeadTerritory,
        retrieveTerritoryOfficeHistory: retrieveTerritoryOfficeHistory,
        retrieveTerritoriesLeadsCount: retrieveTerritoriesLeadsCount,
        retrieveTerritoriesOfficeHistory: retrieveTerritoriesOfficeHistory,
        updateLeadGroupTerritoriesOffices: updateLeadGroupTerritoriesOffices
    };

    return service;

    function retrieveLeadBatches(query: LeadBatchGetRequest) {
        return $http<LeadBatchListResponse[]>({
            method: "GET",
            url: apexApiUrl + "lead-batches",
            params: query
        }).then(function (response) {
            return response.data;
        });
    }

    function retrieveLeadBatchJob(id) {
        return $http({
            method: "GET",
            url: apexApiUrl + "lead-batch-jobs/" + id
        }).then(function (response) {
            return response.data;
        });
    }

    function retrieveLeadBatch(leadBatchID) {
        return $http({
            method: "GET",
            url: apexApiUrl + "lead-batches/" + leadBatchID
        }).then(function (response) {
            return response.data;
        });
    }

    function createLeadBatch(request) {
        return $http({
            method: "POST",
            url: apexApiUrl + "lead-batches",
            headers: { "Content-Type": "application/json" },
            data: request
        }).then(function (response) {
            return response.data;
        });
    }

    function retrieveLeadGroups(query) {
        const params = angular.copy(query);
        if (params.containsDate) {
            params.containsDate = moment(params.containsDate).format("YYYY-MM-DD")
        }

        return $http({
            method: "GET",
            url: apexApiUrl + "lead-groups",
            params: params
        }).then(function (response) {
            return response.data;
        });
    }

    function retrieveLeadGroupLeads(leadGroupID, params) {
        return $http({
            method: "GET",
            url: apexApiUrl + "lead-groups/" + leadGroupID + "/leads",
            params: params
        }).then(function (response) {
            return hydrateLeadMarkerList(response.data);
        }).then(function (leads) {
            // Map the response data into VM format.
            return leads.map(function (lead) {
                const marker = mapLeadSnapshotVM(lead);
                return marker;
            });
        })
    }

    function retrieveLeadGroup(leadGroupID) {
        return $http({
            method: "GET",
            url: apexApiUrl + "lead-groups/" + leadGroupID
        }).then(function (response) {
            return response.data;
        });
    }

    function retrieveLeadGroupTerritories(leadGroupID, params) {
        return $http({
            method: "GET",
            url: apexApiUrl + "lead-groups/" + leadGroupID + "/territories",
            params: params
        }).then(function (response) {
            return response.data;
        });
    }

    function createLeadGroupGeography(request) {
        return $http({
            method: "POST",
            url: apexApiUrl + "lead-groups/" + request.LeadGroupID + "/geographies",
            headers: { "Content-Type": "application/json" },
            data: request
        });
    }

    function updateLeadGroupGeography(request) {
        return $http({
            method: "PUT",
            url: apexApiUrl + "lead-groups/" + request.LeadGroupID + "/geographies/" + request.LeadGroupGeographyID,
            headers: { "Content-Type": "application/json" },
            data: request
        });
    }

    function patchLeadGroup(request) {
        return $http({
            method: "PATCH",
            url: apexApiUrl + "lead-groups/" + request.LeadGroupID,
            headers: { "Content-Type": "application/json" },
            data: request
        });
    }

    function deleteLeadBatch(leadBatchID) {
        return $http({
            method: "DELETE",
            url: apexApiUrl + "lead-batches/" + leadBatchID
        });
    }

    function removeLeadGroupGeography(request) {
        return $http({
            method: "DELETE",
            url: apexApiUrl + "lead-groups/" + request.LeadGroupID + "/geographies/" + request.LeadGroupGeographyID,
            headers: { "Content-Type": "application/json" },
            data: request
        });
    }

    function createLeadTerritory(request) {
        return $http({
            method: "POST",
            url: apexApiUrl + "territories",
            headers: { "Content-Type": "application/json" },
            data: request
        });
    }

    function updateLeadTerritory(request) {
        return $http({
            method: "PUT",
            url: apexApiUrl + "territories/" + request.LeadTerritoryID,
            headers: { "Content-Type": "application/json" },
            data: request
        });
    }

    function removeLeadTerritory(request) {
        return $http({
            method: "DELETE",
            url: apexApiUrl + "territories/" + request.LeadTerritoryID,
            headers: { "Content-Type": "application/json" },
            data: request
        });
    }

    function retrieveLeadTerritoryOffices(leadGroupID, leadTerritoryID) {
        return $http({
            method: "GET",
            url: apexApiUrl + "lead-groups/" + leadGroupID + "/territories/" + leadTerritoryID + "/offices"
        }).then(function (response) {
            return response.data;
        });
    }

    function retrieveLeadGroupGeographiesOffices(leadGroupID) {
        return $http({
            method: "GET",
            url: apexApiUrl + "lead-groups/" + leadGroupID + "/geographies/offices"
        }).then(function (response) {
            return response.data;
        });
    }

    function retrieveLeadGroupLeadsReport(query) {
        return $http({
            method: "GET",
            url: apexApiUrl + "reports/owner-leads",
            headers: { "Content-Type": "application/json" },
            params: query
        }).then(function (response) {
            return response.data;
        });
    }

    function retrieveLeadTerritories(query) {
        return $http({
            method: "GET",
            url: apexApiUrl + "lead-territories",
            headers: { "Content-Type": "application/json" },
            params: query
        }).then(function (response) {
            return response.data;
        });
    }

    function retrieveLeadTerritory(leadTerritoryID) {
        return $http({
            method: "GET",
            url: apexApiUrl + "lead-territories/" + leadTerritoryID,
            headers: { "Content-Type": "application/json" },
        }).then(function (response) {
            return response.data;
        });
    }

    function retrieveTerritoryOfficeHistory(query) {
        return $http({
            method: "GET",
            url: apexApiUrl + "territory-office-history",
            headers: { "Content-Type": "application/json" },
            params: query
        }).then(function (response) {
            return response.data;
        });
    }

    function retrieveTerritoriesLeadsCount(query) {
        return $http({
            method: "GET",
            url: apexApiUrl + "lead-territories/office/" + query.IcdOfficeID,
            headers: { "Content-Type": "application/json" },
            params: query
        }).then(function (response) {
            return response.data;
        });
    }

    function retrieveTerritoriesOfficeHistory(query) {
        return $http({
            method: "GET",
            url: apexApiUrl + "territories-office-history",
            headers: { "Content-Type": "application/json" },
            params: query
        }).then(function (response) {
            return response.data;
        });
    }

    /**
     * Update the shared offices of the territories associated with the given lead group's offices.
     * @param {any} request A request containing leadgroupID their assigned offices
     */
    function updateLeadGroupTerritoriesOffices(request) {
        return $http({
            method: "PATCH",
            url: apexApiUrl + "lead-group/" + request.LeadGroupID + "/territories/offices",
            data: request,
            headers: { "Content-Type": "application/json" }
        });
    }
}
